/**
 * "Is the waiting service worker actually a NEW build?" — shared by the page
 * (`register-sw.ts`) and the worker (`service-worker.ts`). Dependency-free and
 * DOM-free at import time, because the SW bundle imports it too.
 *
 * Why this exists: a waiting worker does NOT imply the user is on an old
 * version. The app shell is served network-first (`service-worker.ts`), so the
 * first open after a deploy already runs the new build while the new worker is
 * still installing behind the old one → it lands in `waiting` and the old
 * logic announced "A new version of Projoint is ready" for the version the user
 * was already looking at (and kept doing so on every open/resume until tapped).
 * Any byte-different reinstall of the same build (a re-registered script, a
 * browser re-fetching a lost script) hit the same false prompt.
 *
 * The check: the page lists the content-hashed `/assets/*` files its own
 * index.html loaded (entry script, modulepreloads, stylesheet); the waiting
 * worker answers whether ALL of them are in its precache manifest. Vite hashes
 * cascade (the entry embeds every reachable chunk's hashed name), so "all
 * present" ⇔ same build. Anything unknown (no reply, old worker, no assets
 * found) counts as "different" → the banner still shows (fail-open).
 */

/** Message type the page posts to the waiting worker (reply via MessageChannel). */
export const BUILD_PROBE_MESSAGE = 'PJ_HAS_PRECACHED';

export interface BuildProbeRequest {
  type: typeof BUILD_PROBE_MESSAGE;
  paths: string[];
}

export interface BuildProbeReply {
  has: boolean;
}

/** `assets/x.js` / `/assets/x.js` → `/assets/x.js` (precache urls are dist-relative). */
function toPath(url: string): string {
  return url.startsWith('/') ? url : `/${url}`;
}

/** The set of paths a worker precaches, from its injected Workbox manifest. */
export function precachedPaths(manifest: ReadonlyArray<string | { url: string }>): Set<string> {
  return new Set(manifest.map((e) => toPath(typeof e === 'string' ? e : e.url)));
}

/** True only when `paths` is non-empty and every path is precached. */
export function hasAllPaths(precached: ReadonlySet<string>, paths: unknown): boolean {
  if (!Array.isArray(paths) || paths.length === 0) return false;
  return paths.every((p) => typeof p === 'string' && precached.has(toPath(p)));
}

/** The content-hashed `/assets/*` files this page's index.html loaded. */
export function currentBuildAssets(doc: Document, origin: string): string[] {
  const nodes = doc.querySelectorAll<HTMLScriptElement | HTMLLinkElement>(
    'script[type="module"][src], link[rel="modulepreload"][href], link[rel="stylesheet"][href]',
  );
  const out = new Set<string>();
  nodes.forEach((n) => {
    const raw = n instanceof HTMLScriptElement ? n.src : n.href;
    if (!raw) return;
    try {
      const u = new URL(raw, origin);
      if (u.origin === origin && u.pathname.startsWith('/assets/')) out.add(u.pathname);
    } catch {
      /* malformed URL — ignore */
    }
  });
  return [...out];
}
